/**
 * Utilitário de compatibilidade para socket.io-client v3.x e v4.x
 * Fornece importação condicional e detecção de versão
 * 
 * Suporta:
 * - socket.io-client v3.x (named exports: io, Socket)
 * - socket.io-client v4.x (default export: io, named export: Socket)
 */

let socketModule: any = null;
let detectedVersion: '3.x' | '4.x' | null = null;

/**
 * Detecta a versão do socket.io-client instalada
 */
export function detectSocketIOVersion(): '3.x' | '4.x' {
  if (detectedVersion) {
    return detectedVersion;
  }

  try {
    // Tenta importar como v4 (default export)
    const v4Module = require('socket.io-client');
    
    // Em v4, io é a função padrão
    if (typeof v4Module === 'function' || typeof v4Module.default === 'function') {
      detectedVersion = '4.x';
      console.log('🔌 Socket.IO Client v4.x detectado');
      return '4.x';
    }
  } catch (e) {
    // Falha silenciosa, tenta v3
  }

  // Se chegou aqui, assume v3
  detectedVersion = '3.x';
  console.log('🔌 Socket.IO Client v3.x detectado');
  return '3.x';
}

/**
 * Importação condicional do socket.io-client
 * Funciona com v3.x e v4.x
 */
export function getSocketIO() {
  if (socketModule) {
    return socketModule;
  }

  try {
    // Tenta v4 primeiro (default export)
    const module = require('socket.io-client');
    
    if (typeof module === 'function') {
      // v4: io é função, Socket é export nomeado
      socketModule = {
        io: module,
        Socket: module.Socket,
        version: '4.x',
      };
    } else if (typeof module.io === 'function') {
      // v3: io é export nomeado
      socketModule = {
        io: module.io,
        Socket: module.Socket,
        version: '3.x',
      };
    } else {
      throw new Error('Socket.IO Client não encontrado ou versão incompatível');
    }

    console.log(`✅ Socket.IO Client ${socketModule.version} carregado`);
    return socketModule;
  } catch (error) {
    console.error('❌ Erro ao carregar Socket.IO Client:', error);
    throw error;
  }
}

/**
 * Hook para usar socket.io com compatibilidade automática
 * Uso: const { io, Socket } = getSocketIOExports();
 */
export function getSocketIOExports() {
  const module = getSocketIO();
  return {
    io: module.io,
    Socket: module.Socket,
    version: module.version,
  };
}

/**
 * Verifica compatibilidade em runtime
 */
export function checkSocketIOCompatibility(): {
  compatible: boolean;
  version: string;
  message: string;
} {
  try {
    const version = detectSocketIOVersion();
    return {
      compatible: true,
      version,
      message: `Socket.IO Client ${version} está disponível e compatível`,
    };
  } catch (error: any) {
    return {
      compatible: false,
      version: 'unknown',
      message: `Socket.IO Client incompatível ou não instalado: ${error.message}`,
    };
  }
}
